/**
 * PortfoliYo Lab — frontend entry (IL-8.3, reworked at PB-4).
 *
 * A **second Vite entry over the same source tree**, not a copied frontend. The three research pages
 * are 4,548 lines between them and share `lib/api`, `types/api` and three components with the Trade
 * SPA; duplicating that would guarantee divergence within a release or two. Importing the very same
 * modules means there is one source of truth, and the split is expressed by which entry imports
 * what. Since PB-1 this entry is the *only* one that routes them.
 *
 * **SDO-1 added the half the product was missing: authoring.** Strategies are written here now,
 * which is why `/strategies` leads the nav — Trade's copy goes at SDO-3.
 *
 * **PB-4 added the half that was missing: a front door.** Until now an unauthenticated visitor got
 * the research shell with every API call failing behind it, because signing in belonged to Trade.
 * Lab owns its sessions now, so it owns `/login` and the guard in front of everything else — and
 * this file no longer knows Trade's origin exists.
 */
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LabAuthProvider, useLabAuth } from './LabAuthContext';
import { LabLayout } from './LabLayout';
import { LabLogin } from './LabLogin';
import { LabStrategies } from './LabStrategies';
import { DefinitionVisualizer } from './DefinitionVisualizer';
import { Backtest } from '../pages/Backtest';
import { WeightAttribution } from '../pages/WeightAttribution';
import { AllocationAnalysis } from '../pages/AllocationAnalysis';
import { AdaptiveAllocation } from './AdaptiveAllocation';

/**
 * Everything except `/login` sits behind this.
 *
 * `loading` renders nothing rather than falling through to the redirect: bouncing an
 * already-signed-in user to `/login` for one frame, before the session check comes back, looks
 * exactly like being signed out and is the single most common way this pattern is got wrong.
 *
 * The current path is carried in `?next=` so a deep link — a bookmarked backtest — survives the
 * round trip through login.
 */
function RequireLabAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useLabAuth();
  const location = useLocation();

  if (loading) return null;
  if (!isAuthenticated) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}

/** Signed in and standing on `/login` — go where they were headed, or to the front door. */
function LoginRoute() {
  const { isAuthenticated, loading } = useLabAuth();
  const location = useLocation();

  if (loading) return null;
  if (isAuthenticated) {
    const next = new URLSearchParams(location.search).get('next');
    // Only same-origin relative paths are honoured. An absolute URL here would make `?next=` an
    // open redirect — a login page that forwards to anywhere is a phishing primitive.
    const target = next && next.startsWith('/') && !next.startsWith('//') ? next : '/backtest';
    return <Navigate to={target} replace />;
  }
  return <LabLogin />;
}

export default function LabApp() {
  return (
    <BrowserRouter>
      <LabAuthProvider>
        <Routes>
          <Route path="/login" element={<LoginRoute />} />

          <Route
            element={
              <RequireLabAuth>
                <LabLayout />
              </RequireLabAuth>
            }
          >
            {/* Reading a definition nobody has saved: text in, tree out, no row created. It is
                first in the nav because it is the one destination here that asks nothing of you
                first — every other one needs a saved strategy to be about. */}
            <Route path="/visualizer" element={<DefinitionVisualizer />} />
            {/* SDO-1: strategy authoring, on Lab's origin and behind Lab's own auth. */}
            <Route path="/strategies" element={<LabStrategies />} />
            <Route path="/backtest" element={<Backtest />} />
            {/* No `liveAttribution` loader, and that is the whole of the attribution fix.
                `GET /api/weight-attribution` reads LIVE Trade positions and is Trade-only by
                decision (D.4.9/D.5.0); Lab's route table excludes it deliberately and Lab
                therefore 404s it — correctly. The page used to initialise in live mode and call
                it on mount anyway. Lab renders the backtest half, over
                `POST /api/weight-attribution/backtest`, which Lab does serve. */}
            <Route path="/attribution" element={<WeightAttribution />} />
            <Route path="/allocation-analysis" element={<AllocationAnalysis />} />
            {/* Performance + risk adaptive sleeve allocation: configure the policy for a sleeve
                portfolio and compare it against the portfolio's fixed and regime allocations. */}
            <Route path="/adaptive" element={<AdaptiveAllocation />} />
            {/* Lab has no dashboard of its own; backtesting is the front door. */}
            <Route path="/" element={<Navigate to="/backtest" replace />} />
            {/* Deliberately no catch-all to a Trade page: an unknown path inside Lab should
                land somewhere Lab actually serves. */}
            <Route path="*" element={<Navigate to="/backtest" replace />} />
          </Route>
        </Routes>
      </LabAuthProvider>
    </BrowserRouter>
  );
}
